import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAtom } from "jotai";
import moment from "moment";
import { getErrorMessage } from "services/api";
import {
  DocumentsData,
  DocumentsOperations,
  DOC_FILTERS,
} from "constant-data/documentsData";
import {
  documentsAtom,
  documentsFiltersAtom,
  documentsFiltersInit,
  selectedDocumentAtom,
  selectedDocumentTypeAtom,
} from "atoms/documentsAtom";
import useDocuments from "features/Documents/useDocuments";
import { useDocReferenceData } from "features/Documents/useDocReferenceData";
import { mapDocumentsToDraw } from "features/Documents/docItemsToDraw";
import * as api from "features/Documents/api";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { Select } from "components/Select";
import { Calendar } from "components/Calendar";
import { SearchInput } from "components/SearchInput";
import { useModal, ConfirmationModal } from "components/Modal";
import { ConductedCellRenderer } from "components/CellRenderers";

import DaysServices from "utils/days";

import styles from "./styles.module.scss";

const DocumentsFilters = ({ inputs, storesOptions }) => {
  const [filters, setFilters] = useAtom(documentsFiltersAtom);

  const patch = (update) =>
    setFilters((prev) => ({ ...prev, page: 1, ...update }));

  const storeVal =
    storesOptions.find(
      (o) => String(o.value) === String(filters.storeid ?? "")
    ) || null;
  const store2Val =
    storesOptions.find(
      (o) => String(o.value) === String(filters.store2id ?? "")
    ) || null;

  if (!inputs) return null;

  return (
    <div className={styles.filters}>
      {inputs.includes(DOC_FILTERS.dateFrom) && (
        <Calendar
          label="Начальная дата"
          value={filters.dateFrom || new Date()}
          onChange={(val) => patch({ dateFrom: DaysServices.getDayStart(val) })}
        />
      )}
      {inputs.includes(DOC_FILTERS.dateTo) && (
        <Calendar
          label="Конечная дата"
          value={filters.dateTo || new Date()}
          onChange={(val) => patch({ dateTo: DaysServices.getDayEnd(val) })}
        />
      )}
      {inputs.includes(DOC_FILTERS.store) && (
        <Select
          label="Склад"
          labelSize={13}
          options={storesOptions}
          value={storeVal}
          onChange={(val) => patch({ storeid: val?.value })}
          placeholder="Все"
          isClearable
        />
      )}
      {inputs.includes(DOC_FILTERS.store2) && (
        <Select
          label="Склад-получатель"
          labelSize={13}
          options={storesOptions}
          value={store2Val}
          onChange={(val) => patch({ store2id: val?.value })}
          placeholder="Все"
          isClearable
        />
      )}
      {inputs.includes(DOC_FILTERS.search) && (
        <div className={styles.search}>
          <SearchInput
            value={filters.search || ""}
            onChange={(val) => patch({ search: val })}
          />
        </div>
      )}
    </div>
  );
};

const DocumentsFooter = () => {
  const {
    createDocumentAndOpen,
    deleteDocument,
    conductDocument,
    unConductDocument,
    openDocument,
    selectedDocument,
  } = useDocuments();
  const { openModal, closeModal } = useModal();

  const onDeleteClick = () => {
    if (!selectedDocument) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить документ с номером "${selectedDocument.docid}"?`}
        onConfirm={deleteDocument}
        onClose={closeModal}
      />
    );
  };

  const onConductClick = () => {
    if (!selectedDocument) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите провести документ с номером "${selectedDocument.docid}"?`}
        onConfirm={conductDocument}
        confirmText="Провести"
        danger={false}
        onClose={closeModal}
      />
    );
  };

  const onUnConductClick = () => {
    if (!selectedDocument) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите отменить проведение документа с номером "${selectedDocument.docid}"?`}
        onConfirm={unConductDocument}
        confirmText="Подтвердить"
        danger={false}
        onClose={closeModal}
      />
    );
  };

  return (
    <ActionsRow>
      <Button
        icon="plus"
        onClick={createDocumentAndOpen}
        title="Создать документ"
      />
      <Button
        icon="delete"
        onClick={onDeleteClick}
        disabled={!selectedDocument}
        title="Удалить документ"
      />
      <Button
        icon="edit"
        variant="tertiary"
        onClick={() => openDocument()}
        disabled={!selectedDocument}
      >
        Редактировать
      </Button>
      {!selectedDocument?.ok ? (
        <Button
          icon="send"
          variant="tertiary"
          onClick={onConductClick}
          disabled={!selectedDocument}
        >
          Провести
        </Button>
      ) : (
        <Button icon="cancel" variant="tertiary" onClick={onUnConductClick}>
          Отмена проведения
        </Button>
      )}
    </ActionsRow>
  );
};

export const DocumentPage = () => {
  const { id: docOperationId } = useParams();

  const { openDocument, getAndSavePrintTemplates } = useDocuments();
  const { storesOptions } = useDocReferenceData();

  const [documents, setDocuments] = useAtom(documentsAtom);
  const [filters, setFilters] = useAtom(documentsFiltersAtom);
  const [selectedDocument, setSelectedDocument] = useAtom(selectedDocumentAtom);
  const [selectedDocumentType, setSelectedDocumentType] = useAtom(
    selectedDocumentTypeAtom
  );

  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState("");

  const loadDocuments = useCallback(async (currentFilters) => {
    setIsLoading(true);
    setLoadError("");
    try {
      const { items } = await api.getDocuments(currentFilters);
      setDocuments(items);
    } catch (e) {
      setLoadError(getErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Смена типа документа: сброс фильтров и загрузка
  useEffect(() => {
    const operation = DocumentsOperations.find(
      (op) => op.id === docOperationId
    );
    if (!operation) return;

    setSelectedDocumentType(operation);
    setSelectedDocument(null);
    setDocuments([]);
    const initial = { ...documentsFiltersInit(), type: docOperationId };
    setFilters(initial);
    getAndSavePrintTemplates(docOperationId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docOperationId]);

  // Загрузка с debounce при изменении фильтров
  useEffect(() => {
    if (!filters.type) return;
    const timeout = setTimeout(() => loadDocuments(filters), 300);
    return () => clearTimeout(timeout);
  }, [filters, loadDocuments]);

  const config = selectedDocumentType?.id
    ? DocumentsData[selectedDocumentType.id]
    : null;

  const documentsToDraw = useMemo(
    () => mapDocumentsToDraw(documents, moment),
    [documents]
  );

  const colDefs = useMemo(
    () =>
      config?.mainColumns?.map((col) =>
        col.field === "isConducted"
          ? { ...col, cellRenderer: ConductedCellRenderer }
          : col
      ),
    [config]
  );

  if (!config) return null;

  return (
    <div className={styles.page}>
      <PageLayout
        header={<BoldText>{selectedDocumentType?.name}</BoldText>}
        footer={<DocumentsFooter />}
        bodyFill
      >
        <DocumentsFilters
          inputs={config.mainFilters}
          storesOptions={storesOptions}
        />
        <Table
          fill
          rowData={documentsToDraw}
          colDefs={colDefs}
          rowKey="docid"
          selectedId={selectedDocument?.docid}
          onSelect={(row) => {
            const doc = documents.find((d) => d.docid === row.docid);
            setSelectedDocument(doc || null);
          }}
          onOpen={(row) => {
            const doc = documents.find((d) => d.docid === row.docid);
            if (doc) openDocument(doc);
          }}
          loading={isLoading && !documents.length}
          error={loadError}
          onRetry={() => loadDocuments(filters)}
          bottomRow={
            config.bottomRow && documentsToDraw.length
              ? config.bottomRow({ documentsToDraw })
              : undefined
          }
        />
      </PageLayout>
    </div>
  );
};
